/**
 * The readings HQ's Seats tab shows, kept out of the component so they can be pinned.
 */

export type SeatPerson = {
    id: number,
    name?: string|null,
    email?: string|null,
    /** The role on **this account**, not the person's global roles array. */
    role?: string|null,
    roleLabel?: string|null,
    /**
     * Whether the address is on one of the account's verified domains.
     *
     * A mark, not a gate. Requiring a match was tried and dropped so that an agency
     * arrangement stays possible later: one firm's staff hold seats on many client
     * accounts, and none of those domains are theirs.
     */
    onVerifiedDomain?: boolean,
};

export type SeatInvitation = {
    id?: string|null,
    email?: string|null,
    name?: string|null,
    role?: string|null,
    expires?: number|null,
};

/**
 * A role on an account, as it should read on screen.
 *
 * Customer roles are a closed two-value vocabulary, separate from the `ROLE_*` hierarchy
 * the admin firewall uses: an account has one `ACCOUNT_OWNER` and any number of
 * `REVIEWER`s. Anything else is shown as it is stored rather than hidden, because a value
 * outside the vocabulary is worth seeing.
 *
 * The API sends a label with the value now, because on the seat table the role belongs to
 * the membership and the API is the one that knows it. This is the fallback for a value
 * that arrives without one.
 */
export function seatRoleLabel(role: string|null|undefined): string {
    if (typeof role !== 'string' || role.trim() === '') {
        return 'No role';
    }
    if (role === 'ACCOUNT_OWNER') {
        return 'Account owner';
    }
    if (role === 'REVIEWER') {
        return 'Reviewer';
    }

    return role;
}

/**
 * An invitation's expiry, which is stored as a unix timestamp in seconds.
 */
export function invitationExpiry(expires: number|null|undefined, now: Date = new Date()): { text: string, expired: boolean } {
    if (typeof expires !== 'number' || !Number.isFinite(expires)) {
        return { text: 'No expiry recorded', expired: false };
    }

    const at: Date = new Date(expires * 1000);
    const expired: boolean = at.getTime() <= now.getTime();

    return {
        text: (expired ? 'Expired ' : 'Expires ') + at.toLocaleString('en-GB', {
            day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
        }),
        expired,
    };
}

export type SeatPanelState = {
    loading: boolean,
    headroom: boolean|undefined,
    candidates: SeatPerson[],
    verifiedDomains: string[],
    /** What the admin has typed, so an empty list can say which kind of empty it is. */
    search: string,
};

/**
 * Why a seat cannot be added right now, or undefined when one can.
 *
 * Each of these is a different problem with a different fix, and an admin who is told
 * only "you cannot add anyone" will go looking in the wrong place. The order matters: a
 * missing verified domain is worth saying even on a full account, because verifying one
 * is the longer job of the two.
 */
export function addSeatBlockedReason(state: SeatPanelState): string|undefined {
    if (state.loading) {
        return 'Reading this account\'s seats.';
    }
    if (state.headroom === false) {
        return 'Every seat this account is billed for is taken. Raise the seat count on the '
            + 'Subscription tab to add another.';
    }
    if (state.candidates.length === 0) {
        return state.search.trim() !== ''
            ? 'No customer matches that search.'
            : 'There is no other customer to give a seat to.';
    }

    return undefined;
}

/**
 * What to say under the picker when it is open.
 *
 * A verified domain is no longer a condition of holding a seat, so this explains the mark
 * rather than a restriction. Saying nothing would leave the warning icon unexplained, and
 * an unexplained warning is worse than none.
 */
export function addSeatGuidance(verifiedDomains: string[]): string {
    if (verifiedDomains.length === 0) {
        return 'This account has no verified domain, so every customer here is marked as '
            + 'unexpected. Verifying a domain on the Company tab is what makes the mark useful.';
    }

    return 'Customers on ' + verifiedDomains.join(' or ') + ' are listed first. Anyone else can '
        + 'still be given a seat and is marked, because seating one customer\'s staff on '
        + 'another\'s account gives them that account\'s data.';
}

/**
 * A person's name for a row, falling back to the address and then to the id, because a
 * row with an empty name reads as a broken row rather than an unnamed person.
 */
export function personLabel(person: SeatPerson): string {
    const name: string = (person.name ?? '').trim();
    if (name !== '') {
        return name;
    }
    const email: string = (person.email ?? '').trim();

    return email !== '' ? email : `Customer #${ person.id }`;
}

/** A change the Seats tab makes, one person at a time, through HQ-API (HQAPI-92). */
export type SeatChange = 'grant' | 'revoke';

/** The seats pool as `GET /hq/customer/account/allowances` reports it. */
export type SeatPool = { used: number, allowed: number, headroom: boolean };

/**
 * What to say when an account already holds more seats than its subscription allows.
 *
 * Accounts like that exist, because nothing enforced the limit on the admin side until
 * 2026-09-10. **Only an increase is refused**: the picker is closed while there is no headroom,
 * and HQ-API refuses a grant on the same numbers, but a seat can always be removed.
 */
export function overSeatAllowance(seats: SeatPool|undefined): string|undefined {
    if (seats === undefined || seats.used <= seats.allowed) {
        return undefined;
    }

    return `This account holds ${ seats.used } seats and its subscription allows ${ seats.allowed }. `
        + 'Nobody else can be given a seat until the seat count on the Subscription tab is raised. '
        + 'Seats can still be removed.';
}

/**
 * The question asked before a seat changes. The change is made the moment it is confirmed, with no
 * form Save after it, so the wording says so.
 */
export function seatChangeConfirmation(change: SeatChange, person: SeatPerson): { title: string, content: string, confirm: string } {
    const who: string = personLabel(person);

    if (change === 'grant') {
        return {
            title: 'Give a seat',
            content: `Give ${ who } a seat on this account? It takes one of the seats the subscription allows, straight away.`,
            confirm: 'Give seat',
        };
    }

    return {
        title: 'Remove a seat',
        content: `Remove ${ who }'s seat on this account? They lose access to the account straight away.`,
        confirm: 'Remove seat',
    };
}
